import React, { useMemo } from "react";
import {
  Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NAVY, BORDER, TEXT_SECONDARY } from "../../theme/tokens";
import { useGetTemplateQuery } from "../../Api/attoDeskApi";
import { ZoneWidget } from "../Player/LayoutRenderer";
import { IPlayerZone } from "../Player/types";

/**
 * Shared preview modal for a template. Loads `/templates/{id}` on
 * open and renders the canvas at 16:9 (or 9:16 for portrait) with
 * absolute-positioned zones — identical to the designer canvas and
 * the Web Player's LayoutRenderer, but read-only.
 *
 * Wired in three places: TemplateList (row action), TemplateEdit
 * (header preview button), Playlist Edit (TEMPLATE row preview).
 */

interface PreviewZone {
  id: number;
  name: string | null;
  x: number; y: number; w: number; h: number;
  z_order: number;
  widget_type: string;
  widget_config: Record<string, any>;
}

interface PreviewTemplate {
  id: number;
  name: string;
  orientation: "landscape" | "portrait";
  canvas_width: number;
  canvas_height: number;
  background: string;
  version: number;
  zones: PreviewZone[];
}

/** Map a raw `/templates/{id}` payload to the preview shape. */
const toPreviewTemplate = (raw: any): PreviewTemplate | null => {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name,
    orientation: raw.orientation,
    canvas_width: raw.canvas_width,
    canvas_height: raw.canvas_height,
    background: raw.background || "#000",
    version: raw.version,
    zones: raw.zones ?? [],
  };
};

/**
 * Loads `/templates/{id}` and renders its full zone layout — the SAME
 * read-only canvas used by TemplatePreviewDialog. Exported so the Playlist
 * preview can show real template content (zones / website / video / widgets)
 * instead of a placeholder, keeping a single template-rendering path. RTK
 * caches by id, so repeat views (e.g. a looping playlist) are instant.
 */
export const TemplatePreviewById: React.FC<{
  templateId: number | null | undefined;
  fallbackName?: string | null;
}> = ({ templateId, fallbackName }) => {
  const { data, isFetching } = useGetTemplateQuery(templateId ?? 0, {
    skip: templateId == null,
  });
  const t = useMemo(() => toPreviewTemplate((data as any)?.data), [data]);

  if (t) {
    return (
      <Box sx={{
        width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "#000",
      }}>
        <PreviewCanvas template={t} />
      </Box>
    );
  }
  return (
    <Box sx={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 1.5, color: "#fff",
      background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
        {fallbackName || (templateId != null ? `Template #${templateId}` : "Template")}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
        {isFetching ? "Loading template…" : "Template could not be loaded"}
      </Typography>
    </Box>
  );
};

const TemplatePreviewDialog: React.FC<{
  templateId: number | null;
  onClose: () => void;
}> = ({ templateId, onClose }) => {
  const open = templateId !== null;
  const { data, isFetching } = useGetTemplateQuery(templateId ?? 0, {
    skip: !open,
  });

  const t: PreviewTemplate | null = useMemo(
    () => toPreviewTemplate((data as any)?.data),
    [data],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{
        display: "flex", alignItems: "center", gap: 1.5, pr: 5,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: NAVY }}>
            {t?.name ?? "Loading…"}
          </Typography>
          {t && (
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.25 }}>
              v{t.version} · {t.canvas_width}×{t.canvas_height} · {t.zones.length} zone{t.zones.length === 1 ? "" : "s"}
            </Typography>
          )}
        </Box>
        <Chip size="small" label={t?.orientation ?? "—"} sx={{ textTransform: "capitalize" }} />
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {isFetching && !t && (
          <Box sx={{ py: 8, textAlign: "center", color: TEXT_SECONDARY }}>
            Loading template…
          </Box>
        )}
        {t && <PreviewCanvas template={t} />}
      </DialogContent>
    </Dialog>
  );
};

export const PreviewCanvas: React.FC<{ template: PreviewTemplate }> = ({ template }) => {
  const aspect = `${template.canvas_width} / ${template.canvas_height}`;
  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: aspect,
        maxHeight: "75vh",
        position: "relative",
        background: template.background,
        border: `1px solid ${BORDER}`,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {[...template.zones]
        .sort((a, b) => (a.z_order ?? 0) - (b.z_order ?? 0))
        .map((z) => (
          <Box
            key={z.id}
            sx={{
              position: "absolute",
              left: `${z.x}%`,
              top: `${z.y}%`,
              width: `${z.w}%`,
              height: `${z.h}%`,
              zIndex: (z.z_order ?? 0) + 1,
              overflow: "hidden",
            }}
          >
            {/* SHARED renderer — identical to device playback. No placeholder
                badges: text/clock/qr/image/video/website/menu_board render
                their real content; only genuinely unconfigured widgets show a
                helpful hint. */}
            <ZoneWidget zone={z as unknown as IPlayerZone} />
          </Box>
        ))}
    </Box>
  );
};

export default TemplatePreviewDialog;
